/**
 * Cálculo de operaciones de factoring — FUENTE ÚNICA (10.1, 10.19, 10.27).
 *
 * Reemplaza la fórmula que vivía duplicada, idéntica, en:
 *   - src/components/Factoring/index.js (NuevaOperacionModal)
 *   - src/components/PlanCuotasModal/index.js (tab Factoring)
 * (era exactamente el patrón de 10.243: dos copias que había que cambiar juntas o el sistema
 * quedaba incoherente consigo mismo).
 *
 * La convención de cálculo es un dato POR PROVEEDOR que vive en `proveedores_factoring`
 * (`tipo_tasa`, `plazo_incluye_dia_operacion`) y se valida contra una liquidación real del
 * proveedor (10.191). NO se asume que la fórmula de un factor aplica a otro:
 *
 *   - tasa VENCIDA     → interés sobre el capital:        diferencia = capital · tasa · dias/30
 *   - tasa ANTICIPADA  → descuento sobre el valor futuro: i = tasa · dias/30 ; diferencia = capital · i/(1+i)
 *
 * Y el conteo de días depende de si el proveedor incluye el día de operación:
 *   - incluyeDiaOperacion = true  → (venc − oper) + 1   (comportamiento histórico, default)
 *   - incluyeDiaOperacion = false → (venc − oper)       (Fingo/Santander, Xepelin: cuenta exclusivo)
 *
 * Antecedente: el modelo Chita con "+1" y tasa vencida se aplicó a Santander durante 4 meses;
 * el PDF de Santander (cesión 689.141) cierra que su tasa es anticipada y su plazo exacto.
 * Ver docs/modulos/factoring.md (regla de módulo) y el diagnóstico en docs/decisiones/.
 *
 * EXCEPCIÓN — Fingo / Santander (P1.17, 2026-09-24): el tipo de tasa NO es fijo por proveedor.
 * Bajo el mismo contrato liquida anticipada (comprobante "Office Banking") o vencida (comprobante
 * "Fintech"), y se sabe recién al ver el comprobante. Para esos proveedores la UI calcula LAS DOS
 * (llamando dos veces a calcularOperacionFactoring, misma fórmula) y el usuario elige; el tipo
 * elegido se guarda en operaciones_factoring.tipo_tasa. Plazo y redondeo siguen por proveedor.
 *
 * PURO: sin fetch, sin estado. Las convenciones y montos entran por parámetro.
 */

// parseFloat("1,0713") devuelve 1 — la coma corta el parsing. Normalizamos coma → punto antes
// de parsear y devolvemos la tasa como FRACCIÓN decimal (1,0713% → 0.010713). Idéntico al
// parsePct que ya vivía inline en los dos modales; centralizado acá.
export const parsePct = (v) => parseFloat(String(v ?? "0").replace(",", ".")) / 100 || 0;

// Proveedores cuyo tipo de tasa se elige POR OPERACIÓN contra el comprobante (P1.17), por ID
// (10.7 — nunca por nombre). id 3 = Fingo / Santander (Chile).
const PROVEEDORES_TASA_POR_COMPROBANTE = new Set([3]);
export const tasaSeEligePorComprobante = (proveedorId) => PROVEEDORES_TASA_POR_COMPROBANTE.has(Number(proveedorId));

// Etiqueta legible de la convención que se está aplicando, para el bloque de cálculos de la UI.
// Que la convención sea visible ANTES de guardar hace que una divergencia contra el PDF del
// proveedor se detecte a tiempo. Ej.: "tasa anticipada · 53 días · redondeo por documento".
// tipoTasa "por_comprobante" = proveedor con dos fórmulas todavía sin elección (P1.17).
const ETIQUETA_TIPO_TASA = { anticipada: "tasa anticipada", por_comprobante: "tasa según comprobante" };
export const describirConvencionFactoring = ({ tipoTasa, redondeoPorDocumento, plazoDias } = {}) => {
  const partes = [ETIQUETA_TIPO_TASA[tipoTasa] || "tasa vencida"];
  if (Number(plazoDias) > 0) partes.push(`${plazoDias} días`);
  if (redondeoPorDocumento) partes.push("redondeo por documento");
  return partes.join(" · ");
};

// Días de plazo de la operación. `incluyeDiaOperacion` decide si se suma el día de operación.
export const calcularPlazoDias = ({ fechaOperacion, fechaVencimiento, incluyeDiaOperacion = true }) => {
  if (!fechaOperacion || !fechaVencimiento) return 0;
  const d1 = new Date(fechaOperacion);
  const d2 = new Date(fechaVencimiento);
  const dias = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return incluyeDiaOperacion ? dias + 1 : dias;
};

// Normaliza un "documento" (número suelto o fila con monto) a un número.
const montoDocumento = (d) =>
  Number(d != null && typeof d === "object" ? (d.monto_total ?? d.monto ?? d.monto_factura ?? 0) : d) || 0;

// Diferencia de precio (costo financiero) de la operación.
//
// Redondeo POR DOCUMENTO: es una convención POR PROVEEDOR (`redondeo_por_documento`), NO algo que
// se dispare por el solo hecho de recibir `documentos`. Cuando el flag está activo y los
// documentos cubren el capital, se redondea CADA documento por separado y se suma; si no, sobre
// el agregado. Importa porque el redondeo por documento cambia el resultado también en tasa
// VENCIDA (±1 peso en ~25% de las ops multi factura): con Santander da 171.298 vs 171.297 del
// agregado (el PDF dice 171.298), pero aplicarlo a Xepelin (que redondea sobre el agregado)
// movería el número de sus ops multi factura al 100% (op 10). Con el flag en false el resultado
// es bit a bit el de hoy para cualquier proveedor, pase o no pase `documentos`.
export const calcularDiferenciaPrecio = ({
  capital,
  tasaMensual,
  dias,
  tipoTasa = "vencida",
  documentos = null,
  redondeoPorDocumento = false,
}) => {
  const cap = Number(capital) || 0;
  const tasa = Number(tasaMensual) || 0;
  const d = Number(dias) || 0;
  if (cap <= 0 || tasa <= 0 || d <= 0) return 0;

  // Diferencia SIN redondear sobre un monto x, según la convención de tasa.
  const difSinRedondear = (x) => {
    if (tipoTasa === "anticipada") {
      const i = (tasa * d) / 30;
      return x * (i / (1 + i));
    }
    // vencida — misma expresión (mismo orden) que la fórmula histórica, para reproducir bit a
    // bit el costo_financiero de las operaciones ya cargadas.
    return d * (tasa / 30) * x;
  };

  const docs = Array.isArray(documentos) ? documentos.map(montoDocumento).filter((m) => m > 0) : [];
  const sumaDocs = docs.reduce((s, m) => s + m, 0);
  // Por documento solo si el proveedor lo usa (flag) Y los documentos cubren exactamente el
  // capital (tolerancia < 1 peso). Sin el flag, siempre agregado.
  if (redondeoPorDocumento && docs.length > 0 && Math.abs(sumaDocs - cap) < 1) {
    return docs.reduce((s, m) => s + Math.round(difSinRedondear(m)), 0);
  }
  return Math.round(difSinRedondear(cap));
};

/**
 * Cálculo completo de una operación de factoring.
 *
 * @param {object} params
 * @param {number} params.monto              - monto total de la(s) factura(s) cedida(s)
 * @param {number} params.pctAnticipo        - fracción de anticipo (0.95, 1, …)
 * @param {number} params.tasaMensual        - tasa mensual como fracción decimal (0.010713)
 * @param {number} [params.comisionProveedor] - fee operativo del proveedor
 * @param {string} params.fechaOperacion     - YYYY-MM-DD
 * @param {string} params.fechaVencimiento   - YYYY-MM-DD (fecha_cobro_final)
 * @param {'vencida'|'anticipada'} [params.tipoTasa]         - convención del proveedor
 * @param {boolean} [params.incluyeDiaOperacion]             - convención del proveedor
 * @param {boolean} [params.redondeoPorDocumento]            - convención del proveedor
 * @param {Array<number|object>} [params.documentos]         - facturas vinculadas (para redondeo por documento)
 * @returns {{ plazoDias, capitalAdelantado, diferenciaPrecio, totalCobros, giroARecibir,
 *   excedentes, montoLiquidacion }}
 */
export const calcularOperacionFactoring = ({
  monto,
  pctAnticipo,
  tasaMensual,
  comisionProveedor = 0,
  fechaOperacion,
  fechaVencimiento,
  tipoTasa = "vencida",
  incluyeDiaOperacion = true,
  redondeoPorDocumento = false,
  documentos = null,
}) => {
  const montoNum = Number(monto) || 0;
  const capitalAdelantado = Math.round(montoNum * (Number(pctAnticipo) || 0));
  const excedentes = montoNum - capitalAdelantado;

  const plazoDias = calcularPlazoDias({ fechaOperacion, fechaVencimiento, incluyeDiaOperacion });
  const diferenciaPrecio = calcularDiferenciaPrecio({
    capital: capitalAdelantado,
    tasaMensual,
    dias: plazoDias,
    tipoTasa,
    documentos,
    redondeoPorDocumento,
  });
  const comision = Number(comisionProveedor) || 0;
  const totalCobros = diferenciaPrecio + comision;
  const giroARecibir = capitalAdelantado - totalCobros;

  return {
    plazoDias,
    capitalAdelantado,
    diferenciaPrecio,
    totalCobros,
    giroARecibir,
    excedentes,
    // MontoLiquid de la liquidación = capital − diferencia (antes de restar la comisión). Útil
    // para el bloque de cálculos de la UI y para reconciliar la cascada contra el PDF.
    montoLiquidacion: capitalAdelantado - diferenciaPrecio,
  };
};
